// imports
import { FC, useEffect, useState } from 'react'

// components
import { Button } from '@bosch/react-frok'

// styles
import './User.scss'

type UserInfo = {
    account?: {
        name?: string
    }
    profilePhoto?: string | null
    roles?: string[]
}

type RoleAssistant = {
    id: string
    name?: string
}

const isRoleAssistant = (assistant: unknown, id: string): assistant is RoleAssistant =>
    typeof assistant === 'object'
    && assistant !== null
    && 'id' in assistant
    && assistant.id === id

type UserProps = {
    user: UserInfo
    t: (key: string) => string
    assistants?: unknown[]
    rehydrateInformation: () => Promise<unknown>
    switchSpace: (spaceId: string | undefined) => void
    navigateTo: (path: string) => void
    logout: () => void
}

const User: FC<UserProps> = ({
    user,
    t,
    navigateTo,
    assistants,
    rehydrateInformation,
    switchSpace,
    logout,
}: UserProps) => {
    const [roles, setRoles] = useState<string[]>(['no roles found'])
    const [isUpdating, setIsUpdating] = useState(false)

    const name = user.account?.name ?? <i>Jane Doe</i>
    const photo = user.profilePhoto ?? 'images/placeholder.svg'

    useEffect(() => {
        if (Array.isArray(user.roles) && user.roles.length > 0)
            setRoles(user.roles)
    }, [user.roles])

    const parseRoleName = (role: string) => {
        if (role == "" || role == "no roles found")
            return <i>{role}</i>

        const assistant = assistants?.find((a): a is RoleAssistant => isRoleAssistant(a, role));
        return assistant?.name ?? role;
    }

    const onLogout = () => {
        switchSpace(undefined)
        logout()
        navigateTo('/identity/login')
    }

    const onRefreshInformation = async () => {
        setIsUpdating(true)

        await rehydrateInformation()

        setIsUpdating(false)
    }

    return (
        <div className='user-card'>
            <div >
                <div className='wrapper-user-data'>
                    <div className='wrapper-user-identity'>
                        <img className='user-photo' src={photo} alt="Profile" />
                        <div className="user-name">{name}</div>
                    </div>
                    <div className='wrapper-user-roles'>
                        <div className="user-role">{t('general:header:role')}:&nbsp;{
                            roles.map((role, index) => {
                                const isLast = index === roles.length - 1
                                const separator = isLast ? '.' : ', '

                                return (
                                    <span key={role}>
                                        {parseRoleName(role)}
                                        {separator}&nbsp;
                                    </span>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </div>
            <div className='actions'>
                <Button
                    label={t('general:buttons:refresh')}
                    className={`user-profile-button update-information-button ${isUpdating ? 'loading' : ''}`}
                    mode="tertiary"
                    icon={{
                        iconName: 'refresh',
                    }}
                    disabled={isUpdating}
                    onClick={onRefreshInformation}
                />
                <Button
                    label={t('general:buttons:settings')}
                    className="user-profile-button settings-button"
                    mode="tertiary"
                    icon={{
                        iconName: 'settings',
                    }}
                    onClick={() => navigateTo('/settings')}
                />
                <Button
                    label={t('general:buttons:logout')}
                    className="user-profile-button logout-button"
                    mode="tertiary"
                    icon={{
                        iconName: 'logout',
                    }}
                    onClick={onLogout}
                />
            </div>
        </div>
    )
}

export default User
